//#region Import
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Fade,
  Slide,
} from '@mui/material';
import {
  SystemUpdateAlt,
  PhoneAndroid,
  PhoneIphone,
} from '@mui/icons-material';
import { Capacitor } from '@capacitor/core';
import Commons from '../../Services/Common/Common';
import {
  getClientVersionPolicy,
  shouldShowUpdateDialog,
} from '../../Services/Common/ClientVersionPolicy';
//#endregion

const DEFAULT_IOS_STORE_URL = 'https://apps.apple.com/app/id6752674705';
const DEFAULT_ANDROID_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.SafeOn.app';

function UpdateDialog() {
  const policy = getClientVersionPolicy();
  const currentVersion = Commons.Storages('get', 'version')?.version;
  const latestVersion = policy?.latestVersion ?? '';

  if (!shouldShowUpdateDialog()) return null;

  const GetPlatform = () => {
    try {
      if (Capacitor?.getPlatform) {
        return Capacitor.getPlatform();
      }
    } catch (_) {}
    return navigator.userAgent.includes('iPhone') ||
      navigator.userAgent.includes('iPad')
      ? 'ios'
      : 'android';
  };

  const HandleUpdate = () => {
    const platform = GetPlatform();
    const fallback =
      platform === 'ios' ? DEFAULT_IOS_STORE_URL : DEFAULT_ANDROID_STORE_URL;
    const url = policy?.storeUrl?.trim() || fallback;
    window.location.href = url;
  };

  const platform = GetPlatform();
  const platformIcon = platform === 'ios' ? <PhoneIphone /> : <PhoneAndroid />;
  const isForceUpdate = Boolean(policy?.forceUpdate);

  return (
    <Dialog
      open={true}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown={isForceUpdate}
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          overflow: 'hidden',
        },
      }}
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up' }}
    >
      {/* 헤더 */}
      <Box
        sx={{
          position: 'relative',
          background: 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255,255,255,0.2)',
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            py: 3,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 60,
              height: 4,
              background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4)',
              borderRadius: 2,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <SystemUpdateAlt sx={{ fontSize: 28, color: '#4ecdc4' }} />
            <Typography variant="h5" fontWeight="bold">
              {isForceUpdate ? '필수 업데이트' : '업데이트 알림'}
            </Typography>
          </Box>
        </DialogTitle>
      </Box>

      {/* 콘텐츠 */}
      <DialogContent sx={{ py: 4, px: 3 }}>
        <Fade in timeout={800}>
          <Card
            sx={{
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: '#333',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography
                  variant="h6"
                  fontWeight="600"
                  color="#2c3e50"
                  gutterBottom
                >
                  {isForceUpdate
                    ? '앱을 계속 사용하려면 업데이트가 필요합니다.'
                    : '새로운 버전이 출시되었습니다! 🎉'}
                </Typography>
                <Typography variant="body1" color="#7f8c8d" sx={{ mb: 2 }}>
                  더 나은 성능과 새로운 기능을 경험해보세요
                </Typography>
              </Box>

              {/* 버전 정보 */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                  p: 2,
                  background:
                    'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                  borderRadius: 2,
                  border: '1px solid rgba(0,0,0,0.05)',
                }}
              >
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography variant="caption" color="#7f8c8d" display="block">
                    현재 버전
                  </Typography>
                  <Chip
                    label={currentVersion || '-'}
                    size="small"
                    sx={{
                      background: '#e74c3c',
                      color: 'white',
                      fontWeight: 'bold',
                      mt: 0.5,
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    width: 40,
                    height: 2,
                    background: 'linear-gradient(90deg, #e74c3c, #27ae60)',
                    borderRadius: 1,
                    mx: 2,
                  }}
                />

                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography variant="caption" color="#7f8c8d" display="block">
                    최신 버전
                  </Typography>
                  <Chip
                    label={latestVersion || '-'}
                    size="small"
                    sx={{
                      background: '#27ae60',
                      color: 'white',
                      fontWeight: 'bold',
                      mt: 0.5,
                    }}
                  />
                </Box>
              </Box>

              {/* 플랫폼 정보 */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  p: 1,
                  background: 'rgba(52, 152, 219, 0.1)',
                  borderRadius: 1,
                  border: '1px solid rgba(52, 152, 219, 0.2)',
                }}
              >
                {platformIcon}
                <Typography variant="body2" color="#3498db" fontWeight="500">
                  {platform === 'ios' ? 'iOS App Store' : 'Google Play Store'}
                  에서 업데이트
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </DialogContent>

      {/* 액션 버튼 */}
      <DialogActions
        sx={{
          p: 3,
          background: 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(255,255,255,0.2)',
        }}
      >
        <Button
          onClick={HandleUpdate}
          variant="contained"
          size="large"
          fullWidth
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 2,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 'bold',
            textTransform: 'none',
            boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              boxShadow: '0 6px 25px rgba(102, 126, 234, 0.6)',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <SystemUpdateAlt sx={{ mr: 1 }} />
          지금 업데이트하기
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UpdateDialog;
